import {
  useCallback,
  useEffect,
  useRef,
  type ClipboardEvent,
  type KeyboardEvent,
} from "react";

/**
 * 6-digit OTP input with auto-advance and full-paste support.
 *
 * - Each box accepts a single digit; typing advances focus
 * - Backspace on an empty box rolls focus back
 * - Pasting a 6-digit string distributes the digits across boxes
 *   and submits the full value
 *
 * Controlled component: parent owns the full `value` string and is
 * notified through {@link onChange}. When `value` reaches 6 digits,
 * {@link onComplete} is fired once.
 */
export interface OtpInputProps {
  value: string;
  onChange: (next: string) => void;
  onComplete?: (full: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
}

const LENGTH = 6;

export default function OtpInput({
  value,
  onChange,
  onComplete,
  disabled,
  autoFocus,
}: OtpInputProps) {
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    if (autoFocus) inputsRef.current[0]?.focus();
  }, [autoFocus]);

  // Fire onComplete exactly once per fully-filled value.
  const completedRef = useRef(false);
  useEffect(() => {
    if (value.length === LENGTH && !completedRef.current) {
      completedRef.current = true;
      onComplete?.(value);
    } else if (value.length < LENGTH) {
      completedRef.current = false;
    }
  }, [value, onComplete]);

  const setDigit = useCallback(
    (index: number, digit: string) => {
      // Take only the first numeric char (handles browsers that pass
      // the entire keystroke buffer e.g. on mobile autofill).
      const cleaned = digit.replace(/\D/g, "").slice(0, 1);
      const chars = value.padEnd(LENGTH, " ").split("");
      chars[index] = cleaned || " ";
      const next = chars.join("").trimEnd();
      onChange(next);
      if (cleaned && index < LENGTH - 1) {
        inputsRef.current[index + 1]?.focus();
      }
    },
    [value, onChange],
  );

  const handleKeyDown = (index: number) => (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      // If the current box is empty, roll back one position.
      const current = value[index] ?? "";
      if (!current) {
        if (index > 0) {
          e.preventDefault();
          // Clear the previous box and focus it.
          const chars = value.padEnd(LENGTH, " ").split("");
          chars[index - 1] = " ";
          onChange(chars.join("").trimEnd());
          inputsRef.current[index - 1]?.focus();
        }
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      inputsRef.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < LENGTH - 1) {
      e.preventDefault();
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, LENGTH);
    if (text.length === 0) return;
    e.preventDefault();
    onChange(text);
    // Focus the last filled box (or the next empty one).
    const focusIndex = Math.min(text.length, LENGTH - 1);
    inputsRef.current[focusIndex]?.focus();
  };

  const digits = Array.from({ length: LENGTH }, (_, i) => value[i] ?? "");

  return (
    <div
      className="otp-input flex justify-center gap-2"
      role="group"
      aria-label="Code à usage unique"
    >
      {digits.map((digit, i) => (
        <input
          key={i}
          ref={(el) => {
            inputsRef.current[i] = el;
          }}
          type="text"
          inputMode="numeric"
          autoComplete={i === 0 ? "one-time-code" : "off"}
          maxLength={1}
          value={digit}
          aria-label={`Chiffre ${i + 1}`}
          disabled={disabled}
          onChange={(e) => setDigit(i, e.target.value)}
          onKeyDown={handleKeyDown(i)}
          onPaste={handlePaste}
          className="border-input bg-bg text-foreground focus-visible:ring-ring/50 size-11 rounded-md border text-center font-mono text-xl outline-none focus-visible:ring-2 disabled:opacity-50"
        />
      ))}
    </div>
  );
}
